/* eslint-disable react/prop-types */
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  AFPTable,
  EmptyState,
  Button,
  FilterTableFrame,
} from '@gsa/afp-component-library';
import PropTypes from 'prop-types';
import BidHistoryTableFilter from './bid-history-table-filters';
import { emDashUnicode } from '../../../utilities/constants';

const BidHistoryTable = ({ bidData }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState([]);
  const [closedBidTableData, setClosedBidTableData] = useState(bidData);
  const solicitationPeriodTypeMap = {
    O: 'Open',
    R: 'Reopen',
    M: 'Mid-cycle',
  };

  const columns = useMemo(() => {
    const columnList = [
      {
        Header: 'Solicitation uPIID',
        accessor: 'soliciation.solicitationNumber',
      },
      {
        Header: 'Title',
        sortable: false,
        accessor: 'soliciation.title',
      },
      {
        Header: 'Posting date',
        accessor: 'solicitationPeriod.startDate',
        Cell: ({ value }) => {
          return moment(value).format('L');
        },
      },
      {
        Header: 'Closing date',
        accessor: 'solicitationPeriod.endDate',
        Cell: ({ value }) => {
          return moment(value).format('L');
        },
      },
      {
        Header: 'Contract year',
        accessor: 'soliciation.contractYear',
      },
      {
        Header: 'Solicitation period',
        accessor: 'solicitationPeriod.periodType',
        Cell: ({ value }) => {
          return solicitationPeriodTypeMap[value] || emDashUnicode;
        },
      },
      {
        Header: 'Actions',
        accessor: 'view_dashboard',
        sortable: false,
        cellClassName: 'afpTable-cell-wordBreak',
        // eslint-disable-next-line
        Cell: ({ row }) => {
          return (
            <Button
              variant="unstyled"
              onClick={() => {
                navigate(
                  `/catalog/solicitations/${
                    // eslint-disable-next-line react/prop-types
                    row?.original?.soliciation?.id
                    // eslint-disable-next-line react/prop-types
                  }/bid-dashboard/${Number(row?.original?.id)}`,
                );
              }}
              label="View Dashboard"
            />
          );
        },
      },
    ];

    return columnList;
  }, [bidData]);

  const filterBids = (items, filters) => {
    const bidFilters = filters.filter((filter) =>
      filter.key.startsWith('soliciation.'),
    );
    if (!bidFilters.length) return items;
    return items.filter((item) =>
      bidFilters.every((filter) => {
        const [_, key] = filter.key.split('.');
        return item.soliciation[key] === filter.value;
      }),
    );
  };
  const filterBidLines = (items, filters) => {
    const bidLineFilters = filters.filter(
      (filter) => !filter.key.startsWith('soliciation.'),
    );
    if (!bidLineFilters.length) return items;
    return items.filter((item) =>
      item.BidLines.some((line) =>
        bidLineFilters.every((filter) => {
          if (filter.key === 'standardItemId')
            return filter.value.includes(line.standardItemId);
          return line[filter.key] === filter.value;
        }),
      ),
    );
  };

  function filterClosedBidData(data, appliedFilters) {
    if (!appliedFilters.length || !data.length) return data;
    const partiallyFilteredData = filterBids(data, appliedFilters);
    const finalFilteredData = filterBidLines(
      partiallyFilteredData,
      appliedFilters,
    );
    return finalFilteredData;
  }

  useEffect(() => {
    const filteredData = filterClosedBidData(bidData, filters);
    setClosedBidTableData(filteredData);
  }, [filters]);

  const header = () => {
    return (
      <div
        className="text-primary title-s-caps padding-top-2"
        data-testid="bids-history-header"
        aria-label="My closed solicitations"
      >
        MY CLOSED SOLICITATIONS
      </div>
    );
  };

  const TableWrapper = ({ tableProps }) => (
    <div className="grid-col">
      <AFPTable {...tableProps} />
      {!tableProps?.data?.length && !tableProps?.isDataLoading && (
        <div className="table-empty-state">
          <EmptyState bottomText="No results found." />
        </div>
      )}
    </div>
  );

  const FTF = useMemo(
    () => FilterTableFrame(header, null, BidHistoryTableFilter, TableWrapper),
    [],
  );

  return (
    <>
      <FTF
        lowLeftProps={{
          filterStructureData: bidData,
          setFilters,
        }}
        lowRightProps={{
          tableProps: {
            testId: 'closed-solicitation-table',
            fullWidth: true,
            columns,
            data: closedBidTableData || [],
          },
        }}
      />
    </>
  );
};

BidHistoryTable.propTypes = {
  bidData: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default BidHistoryTable;
